export enum Languages {
  Rust = "rust",
}

export type Example = {
  title: string
  steps: ExampleStep[]
}

export type TemplateVariables = Record<string, string>

export enum StepType {
  Text = 0,
  ShellCommand = 1,
  InstallDependencies = 2,
  WriteFile = 3,
  WorkDir = 4,
}

export type ExampleStep =
  | TextStep
  | WorkDirStep
  | ShellCommandStep
  | InstallDependenciesStep
  | WriteFileStep

export interface ExampleStepBase {
  title?: string
  description?: string
  type: StepType
}

export interface TextStep extends ExampleStepBase {
  steps: ExampleStep[]
}

export interface WorkDirStep extends ExampleStepBase {
  path: string
}

export interface ShellCommandStep extends ExampleStepBase {
  templateVariables?: string[]
  rust?: string | string[]
}

export interface InstallDependenciesStep extends ExampleStepBase {
  rust?: RustCargoDepMap
}

export interface WriteFileStep extends ExampleStepBase {
  templateVariables?: string[]
  rust?: { path: string; content: string }
}

export interface RustCrateInfo {
  version?: string
  git?: string
  features?: string[]
}

export type RustCargoDepMap = Record<string, string | RustCrateInfo>

export function applyTemplates(
  vars: TemplateVariables,
  eg: Example,
): Promise<Example> {
  return new Promise((resolve, reject) => {
    const newEg = { ...eg, steps: [] as ExampleStepBase[] }
    for (const step of eg.steps) {
      if (step.type === StepType.ShellCommand) {
        const shellStep = step as ShellCommandStep

        // ensure vars has a key for each of the shellStep.templateVariables
        if (shellStep.templateVariables) {
          for (const variable of shellStep.templateVariables) {
            if (!vars[variable]) {
              return reject(
                new Error(
                  `Missing template variable ${variable} for step ${step.title}`,
                ),
              )
            }
          }
        }

        if (shellStep.rust) {
          const commands = Array.isArray(shellStep.rust)
            ? shellStep.rust
            : [shellStep.rust]
          const applied = commands.map((cmd) =>
            applyTemplate(cmd, shellStep.templateVariables || [], vars),
          )
          const newStep = { ...step, rust: applied }
          newEg.steps.push(newStep)
        }
      } else if (step.type === StepType.WriteFile) {
        const writeFileStep = step as WriteFileStep

        // ensure vars has a key for each of the shellStep.templateVariables
        if (writeFileStep.templateVariables) {
          for (const variable of writeFileStep.templateVariables) {
            if (!vars[variable]) {
              return reject(
                new Error(
                  `Missing template variable ${variable} for step ${step.title}`,
                ),
              )
            }
          }
        }

        const newStep = { ...step, rust: {} }
        if (writeFileStep.rust) {
          newStep.rust = {
            path: writeFileStep.rust.path,
            content: applyTemplate(
              writeFileStep.rust.content,
              writeFileStep.templateVariables || [],
              vars,
            ),
          }
        }
        newEg.steps.push(newStep)
      } else {
        newEg.steps.push(step)
      }
    }

    return resolve(newEg)
  })
}

function applyTemplate(
  template: string,
  vars: string[],
  values: TemplateVariables,
): string {
  return vars.reduce((acc, v) => {
    const value = values[v]
    if (!value) {
      throw new Error(`Missing template variable ${v}`)
    }
    return acc.replaceAll(v, value)
  }, template)
}
